import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  TextField,
  MenuItem,
  InputAdornment,
  Pagination,
  FormControlLabel,
} from "@mui/material";
import {
  VerifiedUser,
  Send,
  Notifications,
  Refresh,
  Search as SearchIcon,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";
import StatCard from "../common/StatCard";

const STATUS_COLORS = {
  Verified: "success",
  Pending: "warning",
  Overdue: "error",
  "Not sent": "default",
  NotRequired: "default",
};

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "Not sent", label: "Not sent" },
  { value: "Pending", label: "Pending" },
  { value: "Overdue", label: "Overdue" },
  { value: "Verified", label: "Verified" },
];

const PER_PAGE = 10;

function AssetVerification({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [includeVerified, setIncludeVerified] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [marking, setMarking] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDetail, setCampaignDetail] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [res, campRes] = await Promise.all([
        axios.get("/undertaking/verification/status"),
        axios.get("/undertaking/verification/campaigns").catch(() => ({ data: {} })),
      ]);
      setSummary(res.data.summary || null);
      setItems(res.data.items || []);
      setCampaigns(campRes.data.result || []);
      setSelected([]);
      setPage(1);
    } catch (err) {
      toast.error("Failed to load verification status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter && it.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        it.assignedTo?.empName,
        it.assignedTo?.email,
        it.assignedTo?.empId,
        it.assetTag,
        it.serialNumber,
        it.model,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, statusFilter, search]);

  const pageCount = Math.ceil(filtered.length / PER_PAGE) || 1;
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(() => {
    const start = (safePage - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, safePage]);

  const pageIds = paged.map((it) => it._id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const somePageSelected =
    pageIds.some((id) => selected.includes(id)) && !allPageSelected;

  const toggleAllPage = () => {
    if (allPageSelected) {
      setSelected((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelected([]);

  const startCampaign = async () => {
    setSending(true);
    try {
      const body = {
        ...(selected.length > 0 ? { assetIds: selected } : {}),
        // Never re-send to Verified unless the admin checks the box.
        includeVerified,
      };
      const res = await axios.post("/undertaking/verification/start", body);
      toast.success(res.data.message || "Verification campaign sent.");
      if (res.data.skipped?.length) {
        toast.info(`Skipped ${res.data.skipped.length} already verified.`);
      }
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} link(s) failed to send.`);
      }
      setConfirmOpen(false);
      setIncludeVerified(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to start campaign.");
    } finally {
      setSending(false);
    }
  };

  const sendReminders = async () => {
    setReminding(true);
    try {
      const body = selected.length > 0 ? { assetIds: selected } : {};
      const res = await axios.post("/undertaking/verification/remind", body);
      toast.success(
        `Reminders sent: ${res.data.reminded || 0}. Newly overdue: ${
          res.data.overdue || 0
        }.`
      );
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send reminders.");
    } finally {
      setReminding(false);
    }
  };

  const markSelectedVerified = async () => {
    if (!selected.length) return;
    setMarking(true);
    try {
      const res = await axios.post("/undertaking/verification/mark-verified", {
        assetIds: selected,
      });
      toast.success(res.data.message || "Marked as verified.");
      clearSelection();
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to mark verified.");
    } finally {
      setMarking(false);
    }
  };

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  const confirmCount = selected.length || items.length;
  const confirmScoped = selected.length > 0;

  return (
    <AdminLayout
      title="Asset Verification"
      subtitle="Send and track periodic (e.g. yearly) asset acknowledgments"
      onLogout={handleLogout}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => setConfirmOpen(true)}
            disabled={!items.length}
          >
            {selected.length
              ? `Send verification (${selected.length})`
              : "Send verification now"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Notifications />}
            onClick={sendReminders}
            disabled={reminding || !items.length}
          >
            {reminding
              ? "Sending…"
              : selected.length
                ? `Send reminders (${selected.length})`
                : "Send reminders"}
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<CheckCircle />}
            onClick={markSelectedVerified}
            disabled={marking || selected.length === 0}
          >
            {marking
              ? "Updating…"
              : selected.length
                ? `Mark verified (${selected.length})`
                : "Mark verified"}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="text"
            startIcon={<Refresh />}
            onClick={fetchStatus}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Assigned" value={summary.assigned} icon={<VerifiedUser />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Verified" value={summary.verified} color="success" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Pending" value={summary.pending} color="warning" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Overdue" value={summary.overdue} color="error" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Not sent" value={summary.notRequested} />
            </Grid>
          </Grid>
        )}

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Campaign history
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sent / verified counts for each verification run (yearly audits).
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Campaign</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Sent</TableCell>
                <TableCell align="right">Verified</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Overdue</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.campaignId}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {c.campaignId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {c.startedAt
                      ? new Date(c.startedAt).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell>{c.source || "—"}</TableCell>
                  <TableCell align="right">{c.sent ?? c.sentCount ?? 0}</TableCell>
                  <TableCell align="right">{c.verified ?? 0}</TableCell>
                  <TableCell align="right">{c.pending ?? 0}</TableCell>
                  <TableCell align="right">{c.overdue ?? 0}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={async () => {
                        try {
                          const res = await axios.get(
                            `/undertaking/verification/campaigns/${c.campaignId}`
                          );
                          setCampaignDetail(res.data.result);
                        } catch (err) {
                          toast.error("Failed to load campaign");
                        }
                      }}
                    >
                      Roster
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!campaigns.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No campaigns yet — send verification to create the first report.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <VerifiedUser color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Assigned assets
            </Typography>
            <Chip size="small" label={filtered.length} />
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              placeholder="Search name, email, asset…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {STATUS_FILTERS.map((s) => (
                <MenuItem key={s.value || "all"} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {selected.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                flexWrap: "wrap",
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selected.length} selected
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<Send />}
                onClick={() => setConfirmOpen(true)}
              >
                Send verification
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Notifications />}
                onClick={sendReminders}
                disabled={reminding}
                sx={{
                  color: "inherit",
                  borderColor: "rgba(255,255,255,0.5)",
                  "&:hover": { borderColor: "inherit" },
                }}
              >
                Send reminders
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={clearSelection}
                sx={{ color: "inherit" }}
              >
                Clear
              </Button>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No assigned assets. Assign devices to employees (or import with an
              assignedEmail column) to run a verification campaign.
            </Typography>
          ) : filtered.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No assets match the current search / status filter.
            </Typography>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 860 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allPageSelected}
                          indeterminate={somePageSelected}
                          onChange={toggleAllPage}
                          inputProps={{ "aria-label": "Select all on page" }}
                        />
                      </TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Asset</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell>Last verified</TableCell>
                      <TableCell align="center">Reminders</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((it) => (
                      <TableRow
                        key={it._id}
                        hover
                        selected={selected.includes(it._id)}
                        onClick={() => toggleOne(it._id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selected.includes(it._id)}
                            onChange={() => toggleOne(it._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {it.assignedTo?.empName || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {it.assignedTo?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {it.model || it.assetTag || it.serialNumber || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {it.assetTag || it.serialNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={it.status}
                            color={STATUS_COLORS[it.status] || "default"}
                          />
                        </TableCell>
                        <TableCell>{fmt(it.dueDate)}</TableCell>
                        <TableCell>{fmt(it.lastVerifiedAt)}</TableCell>
                        <TableCell align="center">{it.remindersSent}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(e, v) => setPage(v)}
                />
              </Box>
            </>
          )}
        </Paper>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {confirmScoped
            ? `Send verification to ${confirmCount} selected asset(s)?`
            : "Send verification to assigned employees?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmScoped ? (
              <>
                This emails a secure verification link for each of the{" "}
                <b>{confirmCount}</b> selected asset(s). Assets already{" "}
                <b>Verified</b> are skipped unless you enable the option below.
                Newly sent assets are marked <b>Pending</b> with a due date from
                Settings → Verification.
              </>
            ) : (
              <>
                Emails employees with assigned devices a secure link to
                re-acknowledge. By default, assets already <b>Verified</b> are
                skipped so mid-cycle re-sends do not spam people. Select rows to
                target specific assets, or enable the option below to include
                everyone.
              </>
            )}
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={includeVerified}
                onChange={(e) => setIncludeVerified(e.target.checked)}
              />
            }
            label="Also re-send to already verified assets"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={startCampaign} disabled={sending}>
            {sending ? "Sending…" : "Send now"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(campaignDetail)}
        onClose={() => setCampaignDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Campaign {campaignDetail?.campaign?.campaignId}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`Sent ${campaignDetail?.recipients?.length || 0}`}
            />
            <Chip
              size="small"
              color="success"
              label={`Verified ${
                (campaignDetail?.recipients || []).filter(
                  (r) => r.status === "Verified"
                ).length
              }`}
            />
            <Button
              size="small"
              onClick={() => {
                const rows = campaignDetail?.recipients || [];
                const header = "email,asset,status,sentAt,verifiedAt\n";
                const body = rows
                  .map(
                    (r) =>
                      `"${r.email || ""}","${r.assetLabel || ""}","${
                        r.status || ""
                      }","${r.sentAt || ""}","${r.verifiedAt || ""}"`
                  )
                  .join("\n");
                const blob = new Blob([header + body], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${campaignDetail?.campaign?.campaignId || "campaign"}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export CSV
            </Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(campaignDetail?.recipients || []).map((r) => (
                <TableRow key={`${r.campaignId}-${r.assetId}`}>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.assetLabel}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell>{fmt(r.verifiedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </AdminLayout>
  );
}

export default AssetVerification;
